import { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  Button,
  NativeSelectRoot,
  NativeSelectField,
} from '@chakra-ui/react';
import { MatingRecordFormData, Dog } from '@/types/dog';
import { matingRecordService } from '@/services/firebase/matingRecordService';
import { useDogStore } from '@/stores/dogStore';

interface MatingRecordFormProps {
  userId: string;
  initialData?: MatingRecordFormData & { id?: string };
  onSubmitSuccess?: (recordId: string) => void;
  onCancel?: () => void;
}

/**
 * 交配記録フォームコンポーネント
 */
export const MatingRecordForm: FC<MatingRecordFormProps> = ({
  userId,
  initialData,
  onSubmitSuccess,
  onCancel,
}) => {
  const { dogs, fetchDogs } = useDogStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expectedDate, setExpectedDate] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<MatingRecordFormData & { matingDateStr: string }>();

  const matingDateStr = watch('matingDateStr');

  // 個体一覧取得（親犬選択用）
  useEffect(() => {
    if (userId) {
      fetchDogs(userId);
    }
  }, [userId, fetchDogs]);

  // 初期データのセット
  useEffect(() => {
    if (initialData) {
      setValue('maleId', initialData.maleId);
      setValue('femaleId', initialData.femaleId);
      setValue('matingDateStr', initialData.matingDate.toISOString().split('T')[0]);
    }
  }, [initialData, setValue]);

  // 交配日から出産予定日を計算（交配日 + 63日）
  useEffect(() => {
    if (matingDateStr) {
      const matingDate = new Date(matingDateStr);
      const expectedBirthDate = new Date(matingDate);
      expectedBirthDate.setDate(expectedBirthDate.getDate() + 63);
      setExpectedDate(expectedBirthDate.toLocaleDateString('ja-JP'));
    } else {
      setExpectedDate(null);
    }
  }, [matingDateStr]);

  /**
   * フォーム送信
   */
  const onSubmit = async (data: MatingRecordFormData & { matingDateStr: string }) => {
    setIsLoading(true);
    setError(null);

    try {
      const formData: MatingRecordFormData = {
        maleId: data.maleId,
        femaleId: data.femaleId,
        matingDate: new Date(data.matingDateStr),
      };

      if (initialData?.id) {
        // 更新
        await matingRecordService.updateMatingRecord(initialData.id, formData, userId);
        if (onSubmitSuccess) {
          onSubmitSuccess(initialData.id);
        }
      } else {
        // 新規登録
        const newRecord = await matingRecordService.createMatingRecord(formData, userId);
        if (onSubmitSuccess) {
          onSubmitSuccess(newRecord.id);
        }
      }
    } catch (err) {
      console.error('交配記録保存エラー:', err);
      setError(err instanceof Error ? err.message : '保存に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // メス犬とオス犬の取得
  const femaleDogs = dogs.filter((dog: Dog) => dog.gender === 'female' && dog.status === 'active');
  const maleDogs = dogs.filter((dog: Dog) => dog.gender === 'male' && dog.status === 'active');

  return (
    <Box>
      {error && (
        <Box p={4} bg="red.50" borderRadius="md" borderWidth={1} borderColor="red.200" mb={4}>
          <Text fontSize="sm" color="red.700">
            {error}
          </Text>
        </Box>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <VStack gap={6} align="stretch">
          {/* オス犬選択 */}
          <Box>
            <Text fontSize="sm" fontWeight="medium" mb={2}>
              オス犬（父） <Text as="span" color="red.500">*</Text>
            </Text>
            <NativeSelectRoot>
              <NativeSelectField {...register('maleId', { required: 'オス犬を選択してください' })}>
                <option value="">選択してください</option>
                {maleDogs.map((dog: Dog) => (
                  <option key={dog.id} value={dog.id}>
                    {dog.name} (#{dog.bioNumber})
                  </option>
                ))}
              </NativeSelectField>
            </NativeSelectRoot>
            {errors.maleId && (
              <Text fontSize="sm" color="red.500" mt={1}>
                {errors.maleId.message}
              </Text>
            )}
          </Box>

          {/* メス犬選択 */}
          <Box>
            <Text fontSize="sm" fontWeight="medium" mb={2}>
              メス犬（母） <Text as="span" color="red.500">*</Text>
            </Text>
            <NativeSelectRoot>
              <NativeSelectField {...register('femaleId', { required: 'メス犬を選択してください' })}>
                <option value="">選択してください</option>
                {femaleDogs.map((dog: Dog) => (
                  <option key={dog.id} value={dog.id}>
                    {dog.name} (#{dog.bioNumber})
                  </option>
                ))}
              </NativeSelectField>
            </NativeSelectRoot>
            {errors.femaleId && (
              <Text fontSize="sm" color="red.500" mt={1}>
                {errors.femaleId.message}
              </Text>
            )}
          </Box>

          {/* 交配日 */}
          <Box>
            <Text fontSize="sm" fontWeight="medium" mb={2}>
              交配日 <Text as="span" color="red.500">*</Text>
            </Text>
            <Input
              type="date"
              {...register('matingDateStr', { required: '交配日を選択してください' })}
            />
            {errors.matingDateStr && (
              <Text fontSize="sm" color="red.500" mt={1}>
                {errors.matingDateStr.message}
              </Text>
            )}
            {expectedDate && (
              <Text fontSize="xs" color="gray.600" mt={1}>
                出産予定日（目安）: {expectedDate}（交配日+63日）
              </Text>
            )}
          </Box>

          {/* ボタン */}
          <HStack gap={3}>
            {onCancel && (
              <Button variant="outline" onClick={onCancel} flex={1}>
                キャンセル
              </Button>
            )}
            <Button type="submit" colorPalette="blue" flex={1} loading={isLoading}>
              {initialData?.id ? '更新' : '登録'}
            </Button>
          </HStack>
        </VStack>
      </form>
    </Box>
  );
};
